import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { Button, Chip, CircularProgress, Grid, Tooltip } from '@mui/material';
import { useSnackbar } from 'notistack';
import React from 'react';

import { DeveloperApiError } from '../developer/api';
import { describeError } from '../developer/components';
import { ERROR_CODES } from '../developer/labels';
import { useSandboxSignin } from '../developer/SandboxCard';
import { ContextStore } from '../store/contextStore';

// The environment badge in the dashboard's header (backend metaplan S7b, SBX plan D9): a sandbox's
// dashboard says so on every page, so nobody mistakes it for production. Production shows nothing
// (no change for every production tenant); its Developer pages carry their own environment chip.
const EnvironmentBadge = ({ compact = false }) => {
    const { environment } = React.useContext(ContextStore);
    if (environment !== 'sandbox') {
        return null;
    }
    return (
        <Tooltip title="This is a sandbox: its messages stay in an outbox unless allowlisted" PopperProps={{ style: { zIndex: 99999 } }}>
            <Chip
                data-testid="environment-badge"
                size="small"
                color="warning"
                variant="outlined"
                icon={<ScienceOutlinedIcon sx={{ fontSize: 16 }} />}
                label={compact ? 'SBX' : 'Sandbox'}
                sx={compact ? { '& .MuiChip-label': { px: 0.5 } } : undefined}
            />
        </Tooltip>
    );
};

// Production's half of the switch: sign in to the sandbox (Developer section's sandbox sign-in) and go there.
const SwitchToSandbox = ({ sandbox }) => {
    const signin = useSandboxSignin();
    const snackbar = useSnackbar();
    const busy = signin.busy !== null;
    const go = async () => {
        await signin.open({ landing: 'overview' }, 'switch');
    };
    React.useEffect(() => {
        if (!signin.error) {
            return;
        }
        const mfa = signin.error instanceof DeveloperApiError && signin.error.code === 'mfa_required';
        snackbar?.enqueueSnackbar(mfa ? ERROR_CODES.mfa_required : describeError(signin.error), { variant: mfa ? 'warning' : 'error' });
    }, [signin.error, snackbar]);
    return (
        <Tooltip title={`Open the sandbox ${sandbox.name} (${sandbox.code}) in this tab; you stay signed in here.`} PopperProps={{ style: { zIndex: 99999 } }}>
            <span>
                <Button
                    data-testid="switch-to-sandbox"
                    size="small"
                    variant="outlined"
                    color="warning"
                    disabled={busy}
                    onClick={go}
                    startIcon={busy ? <CircularProgress size={14} /> : <ScienceOutlinedIcon sx={{ fontSize: 16 }} />}
                >
                    Switch to sandbox
                </Button>
            </span>
        </Tooltip>
    );
};

// The header's Production <-> Sandbox switch (DSEC D3; plan §2, the "test mode" toggle). On a sandbox: the badge and,
// in the open menu, "Production" -- a plain link to the production dashboard, whose own session is still there (a
// sandbox session never signs anyone in to production). On production with a sandbox, for a user holding the explicit
// developer.sandbox_signin: "Switch to sandbox" in the open menu. Anywhere else nothing: a production tenant without a
// sandbox, or a user without the permission, sees no change.
export const EnvironmentSwitch = ({ compact = false }) => {
    const { environment, pairedEnvironment, hasExplicitPerm } = React.useContext(ContextStore);
    const onSandbox = environment === 'sandbox';
    const production = onSandbox && pairedEnvironment?.environment === 'production' ? pairedEnvironment : null;
    const sandbox = !onSandbox && pairedEnvironment?.environment === 'sandbox' ? pairedEnvironment : null;
    const canSwitch = !compact && !!sandbox && !!hasExplicitPerm?.('developer', 'sandbox_signin');
    if (!onSandbox && !canSwitch) {
        return null;
    }
    return (
        <Grid container justifyContent={compact ? 'center' : 'flex-start'} alignItems="center" columnGap={1} pl={compact ? 0 : 2.5} pt={1} data-testid="environment-switch">
            {onSandbox ? <EnvironmentBadge compact={compact} /> : <SwitchToSandbox sandbox={sandbox} />}
            {onSandbox && production && !compact && (
                <Tooltip title={`Back to ${production.name}'s production dashboard`} PopperProps={{ style: { zIndex: 99999 } }}>
                    <Button data-testid="switch-to-production" size="small" href={production.dashboard_url}>Production</Button>
                </Tooltip>
            )}
        </Grid>
    );
};

export default EnvironmentBadge;
